import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Search, MessageSquare, Trash2, AlertTriangle, ShieldBan, ShieldOff, Send, Flag, Eye, X } from 'lucide-react';
import { Chevron, EmptyState } from './shared';

export function MessagesTab({ conversations, onRefresh }) {
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [warningText, setWarningText] = useState('');
  const [showWarning, setShowWarning] = useState(null);
  const [reports, setReports] = useState([]);
  const [showReports, setShowReports] = useState(false);
  const [loadingReports, setLoadingReports] = useState(false);

  const filtered = conversations.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [c.buyer?.name, c.buyer?.email, c.seller?.name, c.seller?.email, c.listing_title].some(f => f?.toLowerCase().includes(q));
  });

  const handleDeleteMessage = async (msgId) => {
    try {
      await api.delete(`/admin/messages/${msgId}`);
      toast.success('Message supprimé');
      if (onRefresh) onRefresh();
    } catch { toast.error('Erreur'); }
  };

  const handleDeleteConversation = async (convId) => {
    if (!window.confirm('Supprimer cette conversation et tous ses messages ?')) return;
    try {
      await api.delete(`/admin/conversations/${convId}`);
      toast.success('Conversation supprimée');
      setExpandedId(null);
      if (onRefresh) onRefresh();
    } catch { toast.error('Erreur'); }
  };

  const handleSendWarning = async (convId) => {
    if (!warningText.trim()) return;
    try {
      await api.post(`/admin/conversations/${convId}/warn`, { message: warningText.trim() });
      toast.success('Avertissement envoyé');
      setWarningText('');
      setShowWarning(null);
      if (onRefresh) onRefresh();
    } catch { toast.error('Erreur'); }
  };

  const handleBlockMessaging = async (userId, block) => {
    try {
      if (block) {
        await api.post(`/admin/users/${userId}/block-messaging`);
        toast.success('Messagerie bloquée');
      } else {
        await api.post(`/admin/users/${userId}/unblock-messaging`);
        toast.success('Messagerie débloquée');
      }
    } catch { toast.error('Erreur'); }
  };

  const loadReports = async () => {
    setLoadingReports(true);
    try {
      const { data } = await api.get('/admin/reports');
      setReports(data.reports || []);
    } catch { toast.error('Erreur chargement signalements'); }
    finally { setLoadingReports(false); }
  };

  const handleReportAction = async (reportId, status) => {
    try {
      await api.put(`/admin/reports/${reportId}`, { status });
      toast.success('Signalement mis à jour');
      loadReports();
    } catch { toast.error('Erreur'); }
  };

  return (
    <div className="space-y-4">
      {/* Header with tabs */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input placeholder="Rechercher par utilisateur, email ou annonce..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="conv-search-input" />
        </div>
        <Button
          variant={showReports ? 'default' : 'outline'}
          size="sm"
          onClick={() => { setShowReports(!showReports); if (!showReports) loadReports(); }}
          className={showReports ? 'bg-red-500 hover:bg-red-600 text-white' : ''}
          data-testid="toggle-reports-btn"
        >
          <Flag className="w-4 h-4 mr-1.5" /> Signalements
        </Button>
      </div>

      {/* Reports panel */}
      {showReports && (
        <div className="bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800 p-4 space-y-3" data-testid="reports-panel">
          <h3 className="font-bold text-red-700 dark:text-red-400 text-sm flex items-center gap-2">
            <Flag className="w-4 h-4" /> Signalements ({reports.length})
          </h3>
          {loadingReports ? (
            <p className="text-sm text-neutral-500 text-center py-4">Chargement...</p>
          ) : reports.length === 0 ? (
            <p className="text-sm text-neutral-500 text-center py-4">Aucun signalement</p>
          ) : (
            reports.map(r => (
              <div key={r.id} className="bg-white dark:bg-neutral-800 rounded-lg border p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`report-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-neutral-900 dark:text-white">
                    {r.reporter?.first_name} {r.reporter?.last_name} <span className="text-neutral-400">a signalé</span>
                  </p>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-0.5">"{r.reason}"</p>
                  <p className="text-xs text-neutral-400 mt-1">{new Date(r.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                    r.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                    r.status === 'reviewed' ? 'bg-emerald-100 text-emerald-700' :
                    'bg-neutral-100 text-neutral-600'
                  }`}>{r.status === 'pending' ? 'En attente' : r.status === 'reviewed' ? 'Traité' : r.status}</span>
                  {r.status === 'pending' && (
                    <>
                      <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => handleReportAction(r.id, 'reviewed')} data-testid={`report-review-${r.id}`}>
                        Marquer traité
                      </Button>
                      <Button size="sm" variant="outline" className="text-xs h-7 text-red-500 border-red-200" onClick={() => handleReportAction(r.id, 'dismissed')} data-testid={`report-dismiss-${r.id}`}>
                        Rejeter
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Conversations */}
      {filtered.length === 0 ? <EmptyState icon={MessageSquare} text="Aucune conversation" /> : (
        <div className="space-y-3">
          {filtered.map((conv) => (
            <div key={conv.id} className="bg-white dark:bg-neutral-800 rounded-xl border dark:border-neutral-700 overflow-hidden" data-testid={`admin-conv-${conv.id}`}>
              <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-750 transition-colors" onClick={() => setExpandedId(expandedId === conv.id ? null : conv.id)}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-[#1F3F72] text-white flex items-center justify-center font-bold text-xs shrink-0">{conv.message_count}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 text-sm"><span className="font-semibold text-neutral-900 dark:text-white truncate">{conv.buyer?.name || '?'}</span><span className="text-neutral-400 shrink-0">&harr;</span><span className="font-semibold text-neutral-900 dark:text-white truncate">{conv.seller?.name || '?'}</span></div>
                    <p className="text-xs text-[#F25C05] font-medium truncate mt-0.5">{conv.listing_title}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="text-right">
                    <p className="text-xs text-neutral-400">{conv.last_message_at ? new Date(conv.last_message_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}</p>
                    <p className="text-xs text-neutral-500 truncate max-w-[200px]">{conv.last_message}</p>
                  </div>
                  <Chevron open={expandedId === conv.id} />
                </div>
              </div>
              {expandedId === conv.id && (
                <div className="border-t dark:border-neutral-700 bg-neutral-50/50 dark:bg-neutral-850 px-4 py-4">
                  {/* Users info + actions */}
                  <div className="grid grid-cols-2 gap-4 mb-4 text-xs">
                    <div className="bg-white dark:bg-neutral-700 rounded-lg border dark:border-neutral-600 p-3">
                      <span className="text-neutral-400 uppercase tracking-wide">Acheteur</span>
                      <p className="font-semibold text-neutral-900 dark:text-white mt-0.5">{conv.buyer?.name}</p>
                      <p className="text-neutral-500">{conv.buyer?.email}</p>
                      <div className="flex gap-1.5 mt-2">
                        <button onClick={(e) => { e.stopPropagation(); handleBlockMessaging(conv.buyer?.id, true); }} className="text-[10px] text-red-500 hover:text-red-700 flex items-center gap-0.5" data-testid={`block-buyer-${conv.id}`}>
                          <ShieldBan className="w-3 h-3" /> Bloquer msg
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleBlockMessaging(conv.buyer?.id, false); }} className="text-[10px] text-emerald-500 hover:text-emerald-700 flex items-center gap-0.5" data-testid={`unblock-buyer-${conv.id}`}>
                          <ShieldOff className="w-3 h-3" /> Débloquer
                        </button>
                      </div>
                    </div>
                    <div className="bg-white dark:bg-neutral-700 rounded-lg border dark:border-neutral-600 p-3">
                      <span className="text-neutral-400 uppercase tracking-wide">Vendeur</span>
                      <p className="font-semibold text-neutral-900 dark:text-white mt-0.5">{conv.seller?.name}</p>
                      <p className="text-neutral-500">{conv.seller?.email}</p>
                      <div className="flex gap-1.5 mt-2">
                        <button onClick={(e) => { e.stopPropagation(); handleBlockMessaging(conv.seller?.id, true); }} className="text-[10px] text-red-500 hover:text-red-700 flex items-center gap-0.5" data-testid={`block-seller-${conv.id}`}>
                          <ShieldBan className="w-3 h-3" /> Bloquer msg
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleBlockMessaging(conv.seller?.id, false); }} className="text-[10px] text-emerald-500 hover:text-emerald-700 flex items-center gap-0.5" data-testid={`unblock-seller-${conv.id}`}>
                          <ShieldOff className="w-3 h-3" /> Débloquer
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Admin actions */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Button size="sm" variant="outline" className="text-xs h-8" onClick={() => setShowWarning(showWarning === conv.id ? null : conv.id)} data-testid={`warn-btn-${conv.id}`}>
                      <AlertTriangle className="w-3.5 h-3.5 mr-1 text-amber-500" /> Envoyer un avertissement
                    </Button>
                    <Button size="sm" variant="outline" className="text-xs h-8 text-red-500 border-red-200 hover:bg-red-50" onClick={() => handleDeleteConversation(conv.id)} data-testid={`delete-conv-${conv.id}`}>
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Supprimer la conversation
                    </Button>
                  </div>

                  {/* Warning form */}
                  {showWarning === conv.id && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800 p-3 mb-4 space-y-2" data-testid={`warning-form-${conv.id}`}>
                      <p className="text-xs font-semibold text-amber-700 dark:text-amber-400">Envoyer un avertissement dans cette conversation</p>
                      <Textarea
                        placeholder="Ex: Votre comportement est inapproprié. Merci de respecter les règles de la plateforme."
                        value={warningText}
                        onChange={(e) => setWarningText(e.target.value)}
                        rows={2}
                        className="text-sm"
                        data-testid={`warning-input-${conv.id}`}
                      />
                      <div className="flex gap-2">
                        <Button size="sm" className="text-xs h-7 bg-amber-500 hover:bg-amber-600 text-white" onClick={() => handleSendWarning(conv.id)} disabled={!warningText.trim()} data-testid={`send-warning-${conv.id}`}>
                          <Send className="w-3 h-3 mr-1" /> Envoyer
                        </Button>
                        <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => { setShowWarning(null); setWarningText(''); }}>
                          Annuler
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Messages */}
                  <div className="bg-white dark:bg-neutral-700 rounded-lg border dark:border-neutral-600 p-4 max-h-96 overflow-y-auto space-y-2">
                    {conv.messages?.length === 0 ? <p className="text-center text-neutral-400 text-xs py-4">Aucun message</p> : conv.messages?.map((msg) => {
                      const isBuyer = msg.sender_id === conv.buyer?.id;
                      const isAdmin = msg.sender_id === 'admin' || msg.is_admin_warning;
                      const isDeleted = msg.deleted_by_admin;
                      return (
                        <div key={msg.id} className={`flex ${isAdmin ? 'justify-center' : isBuyer ? 'justify-start' : 'justify-end'}`} data-testid={`admin-msg-${msg.id}`}>
                          {isAdmin ? (
                            <div className="bg-amber-100 dark:bg-amber-900/40 border border-amber-300 dark:border-amber-700 rounded-xl px-4 py-2 max-w-[85%]">
                              <p className="text-xs font-bold text-amber-700 dark:text-amber-400 mb-0.5">Administrateur</p>
                              <p className="text-sm text-amber-800 dark:text-amber-300">{msg.content}</p>
                              <p className="text-[10px] text-amber-500 mt-1">{msg.created_at ? new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''}</p>
                            </div>
                          ) : (
                            <div className={`group relative max-w-[75%] rounded-xl px-3 py-2 ${isDeleted ? 'opacity-50' : ''} ${isBuyer ? 'bg-neutral-100 dark:bg-neutral-600 text-neutral-900 dark:text-white' : 'bg-[#1F3F72] text-white'}`}>
                              <p className="text-[10px] font-semibold mb-0.5 opacity-70">{msg.sender_name || (isBuyer ? conv.buyer?.name : conv.seller?.name)}</p>
                              {msg.image && (
                                <a href={msg.image} target="_blank" rel="noopener noreferrer" className="block mb-1">
                                  <img src={msg.image} alt="" className="rounded-lg max-w-full max-h-40 object-cover" />
                                </a>
                              )}
                              <p className="text-sm leading-relaxed whitespace-pre-line">{msg.content}</p>
                              <div className={`flex items-center justify-end gap-1 mt-1 ${isBuyer ? 'text-neutral-400' : 'text-blue-200'}`}>
                                <span className="text-[10px]">{msg.created_at ? new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                              </div>
                              {/* Delete button */}
                              {!isDeleted && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleDeleteMessage(msg.id); }}
                                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                                  title="Supprimer ce message"
                                  data-testid={`delete-msg-${msg.id}`}
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
